<template>
  <div class="themeArea">
    <div class="themeScreenLoading" v-if="!dataLoaded">
      <div class="lds-ellipsis">
        <div></div>
        <div></div>
        <div></div>
        <div></div>
      </div>
    </div>
    <div
      class="themeScreen"
      v-if="!hasScheduledActivity && dataLoaded"
      :style="{ backgroundColor: theme.color }"
      id="themeScreen"
    >
      <h1 class="themeTitle">{{ theme.name }}</h1>
      <div
        aria-label="activities completed"
        class="theme-progress-area"
        v-if="!isSpeedyKid"
        role="progressbar"
        :aria-valuemax="curriculumPosition.progress.theme.total"
        :aria-valuenow="curriculumPosition.progress.theme.position"
      >
        <div class="themeIconWrapper">
          <img class="themeIcon" id="iconOutline" />
          <img class="themeIcon" id="themeIcon" />
        </div>

        <div class="theme-progress-container">
          <divider
            v-for="item in progressDivisions"
            :key="item.key"
            :bordercolor="theme.color"
            :awardname="theme.awardName"
            :progress-bg="item.progressBg"
            :progress="item.progress"
          ></divider>
        </div>
      </div>
      <div class="theme-progress-area" v-else>
        <div class="speedyGoodJob">
          <p>You're doing a great job, {{ username }}!</p>
        </div>
      </div>

      <button
        @click="goToActivity"
        aria-label="Continue"
        class="theme-button-continue"
        tabindex="0"
        v-html="button"
      ></button>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
import { isEmpty } from 'lodash';

import btnContinue from 'svg-components/theme-progress-btn.svg';
import progressBar from 'assets/svg/theme-progress-area.svg';

// progres bar
import pbar1 from 'images/progress-scribble-bar-color1.gif';
import pbar2 from 'images/progress-scribble-bar-color2.gif';
import pbar3 from 'images/progress-scribble-bar-color3.gif';
import pbar4 from 'images/progress-scribble-bar-color4.gif';
import pbar5 from 'images/progress-scribble-bar-color5.gif';
import pbar6 from 'images/progress-scribble-bar-color6.gif';
import pbarBlocker from 'images/progress-scribble-bar-blocker.gif';

// bg image
import themeJumpIntoKeyboarding from 'images/themes/jumpintokeyboarding.png';
import themeLetsEat from 'images/themes/letseat.png';
import themeOffToWork from 'images/themes/offtowork.png';
import themeOnGo from 'images/themes/onthego.png';
import themeWildAboutAnimals from 'images/themes/wildaboutanimals.png';
import themeLetsPlay from 'images/themes/letsplay.png';
import themeStartMusic from 'images/themes/startthemusic.png';
import themeMathMix from 'images/themes/mathmix.png';
import themeSingPlay from 'images/themes/singandplay.png';
import themeFamousFaces from 'images/themes/famousfaces.png';
import themeGreekLatin from 'images/themes/greekandlatin.png';
import themeBonesBodies from 'images/themes/bonesandbodies.png';
import themeGreatGrammar from 'images/themes/greatgrammar.png';
import themeOhLook from 'images/themes/ohlook.png';
import themeGoGeography from 'images/themes/gogeography.png';
import themeWordsWriters from 'images/themes/worksandwriters.png';
import themeSuperWords from 'images/themes/superwords.png';
import themePeoplePower from 'images/themes/peoplepower.png';
import themeWaterWater from 'images/themes/waterwater.png';
import themeArtStart from 'images/themes/artstart.png';
import themeSpeedyKid from 'images/themes/speedykeyboardkid.png';

// icons
import iconOutline from 'images/theme-icons/iconbg.png';
import iconWildAboutAnimals from 'images/theme-icons/icon-animals.png';
import iconBonesBodies from 'images/theme-icons/icon-bonesbodies.png';
import iconLetsEat from 'images/theme-icons/icon-food.png';
import iconGoGeography from 'images/theme-icons/icon-geography.png';
import iconGreatGrammar from 'images/theme-icons/icon-grammar.png';
import iconGreekLatin from 'images/theme-icons/icon-greeklatin.png';
import iconOffToWork from 'images/theme-icons/icon-jobs.png';
import iconJump from 'images/theme-icons/icon-jump.png';
import iconMathMix from 'images/theme-icons/icon-mathmix.png';
import iconStartMusic from 'images/theme-icons/icon-music.png';
import iconOhLook from 'images/theme-icons/icon-ohlook.png';
import iconPeoplePower from 'images/theme-icons/icon-peoplepower.png';
import iconSingPlay from 'images/theme-icons/icon-singplay.png';
import iconLetsPlay from 'images/theme-icons/icon-sports.png';
import iconSuperWords from 'images/theme-icons/icon-superwords.png';
import iconOnGo from 'images/theme-icons/icon-travel.png';
import iconWaterWater from 'images/theme-icons/icon-waterwater.png';
import iconWordsWriters from 'images/theme-icons/icon-wordswriters.png';

import divider from './divider.vue';
import MetaHelper from '../../mixins/MetaHelper';

export default {
  data() {
    return {
      button: btnContinue,
      progressArea: progressBar,
      pbarArray: [pbar1, pbar2, pbar3, pbar4, pbar5, pbar6],
      blocker: pbarBlocker,
      hasScheduledActivity: true,
      icon: null
    };
  },
  mixins: [MetaHelper],
  components: {
    divider
  },

  computed: {
    ...mapState('student', ['username', 'curriculum']),

    ...mapState('student', {
      sessionId: state => state.sessionId,
      curriculumPosition: state => state.position.next
    }),

    ...mapState('activity', ['data', 'dataLoaded']),

    ...mapState('activity', {
      scheduledActivityId: state => state.data.scheduledActivityId,
      isSpotCheck: state => Boolean(state.data.spotCheck)
    }),
    ...mapGetters('student', { theme: 'nextTheme' }),
    ...mapGetters('activity', ['homeRowVideo', 'activityTitle']),

    pbarColor() {
      return this.theme.index !== undefined ? this.pbarArray[this.theme.index] : '';
    },

    isSpeedyKid() {
      return this.theme.isSpeedyKid;
    },

    progressDivisions() {
      const {
        progress: { theme: { weeksPosition, weeksTotal }, week: { position, total } } = {}
      } = this.curriculumPosition;

      const divisions = [];
      for (let i = 1; i <= weeksTotal; i++) {
        let progress = 0;
        if (i < weeksPosition) {
          progress = 1;
        } else if (i === weeksPosition) {
          progress = (position - 1) / total;
        }

        divisions.push({
          key: `division-${i}`,
          progressBg: this.pbarColor,
          blockerBg: this.blocker,
          progress
        });
      }

      return divisions;
    }
  },

  async mounted() {
    this.setKwtMode('curriculum');

    if (isEmpty(this.curriculum)) {
      await this.getCurriculum();
    }

    await this.getNextCurriculumActivity();

    if (this.homeRowVideo) {
      this.setModal({
        type: 'homeRowVideo',
        data: this.homeRowVideo
      });
    }

    this.hasScheduledActivity = !!this.scheduledActivityId;
    if (this.hasScheduledActivity || this.isSpotCheck) {
      this.goToActivity();
    } else {
      this.$nextTick(() => this.showTheme());
    }
  },

  methods: {
    ...mapActions('student', ['getCurriculum']),
    ...mapActions('activity', ['getNextCurriculumActivity']),
    ...mapMutations('activity', ['setSpotCheckRetries']),
    ...mapMutations('application', ['setModal', 'setKwtMode']),

    showTheme() {
      const { theme: { id: currentThemeId = '' } = {} } = this.curriculumPosition || {};
      this.setThemeImage(currentThemeId);
    },

    setThemeImage(currentTheme) {
      let bgImage = '';
      let icon = '';

      switch (currentTheme) {
        case 'SpeedyKid':
          bgImage = themeSpeedyKid;
          break;

        case 'JumpIntoKeyboarding':
          bgImage = themeJumpIntoKeyboarding;
          icon = iconJump;
          break;

        case 'LetsEat':
          bgImage = themeLetsEat;
          icon = iconLetsEat;
          break;

        case 'OffToWork':
          bgImage = themeOffToWork;
          icon = iconOffToWork;
          break;

        case 'OnTheGo':
          bgImage = themeOnGo;
          icon = iconOnGo;
          break;

        case 'WildAboutAnimals':
          bgImage = themeWildAboutAnimals;
          icon = iconWildAboutAnimals;
          break;

        case 'LetsPlay':
          bgImage = themeLetsPlay;
          icon = iconLetsPlay;
          break;

        case 'StartMusic':
          bgImage = themeStartMusic;
          icon = iconStartMusic;
          break;

        case 'MathMix':
          bgImage = themeMathMix;
          icon = iconMathMix;
          break;

        case 'SingPlay':
          bgImage = themeSingPlay;
          icon = iconSingPlay;
          break;

        case 'FamousFaces':
          bgImage = themeFamousFaces;
          icon = iconPeoplePower;
          break;

        case 'GreekLatin':
          bgImage = themeGreekLatin;
          icon = iconGreekLatin;
          break;

        case 'BonesBodies':
          bgImage = themeBonesBodies;
          icon = iconBonesBodies;
          break;

        case 'GreatGrammar':
          bgImage = themeGreatGrammar;
          icon = iconGreatGrammar;
          break;

        case 'OhLook':
          bgImage = themeOhLook;
          icon = iconOhLook;
          break;

        case 'GoGeography':
          bgImage = themeGoGeography;
          icon = iconGoGeography;
          break;

        case 'WordsWriters':
          bgImage = themeWordsWriters;
          icon = iconWordsWriters;
          break;

        case 'SuperWords':
          bgImage = themeSuperWords;
          icon = iconSuperWords;
          break;

        case 'PeoplePower':
          bgImage = themePeoplePower;
          icon = iconPeoplePower;
          break;

        case 'WaterWater':
          bgImage = themeWaterWater;
          icon = iconWaterWater;
          break;

        case 'ArtStart':
          bgImage = themeArtStart;
          icon = iconOhLook;
          break;

        default:
          break;
      }

      this.icon = icon;

      if (bgImage !== '') {
        const introBg = document.getElementById('themeScreen');
        introBg.style.backgroundImage = `url(${bgImage})`;
      }

      if (icon !== '') {
        const themeIcon = document.getElementById('themeIcon');
        themeIcon.src = icon;
        const iconOutlined = document.getElementById('iconOutline');
        iconOutlined.src = iconOutline;
      }
    },

    goToActivity() {
      if (!this.dataLoaded) return;

      if (this.isSpotCheck) this.setSpotCheckRetries(2);
      else this.setSpotCheckRetries(0);
      const { activityTitle: { name: activityTitle } = {} } = this;
      this.$router.replace({ path: `/activities/${activityTitle}` });
    }
  }
};
</script>

<style lang="scss" scoped>
.themeScreenLoading {
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
}

.lds-ellipsis {
  display: block;
  position: relative;
  width: 64px;
  height: 64px;
}

.lds-ellipsis div {
  position: absolute;
  top: 27px;
  width: 11px;
  height: 11px;
  border-radius: 50%;
  animation-timing-function: cubic-bezier(0, 1, 1, 0);
}

.lds-ellipsis div:nth-child(1) {
  background: #aaa;
  left: 6px;
  animation: lds-ellipsis1 0.6s infinite;
}

.lds-ellipsis div:nth-child(2) {
  background: #bbb;
  left: 6px;
  animation: lds-ellipsis2 0.6s infinite;
}

.lds-ellipsis div:nth-child(3) {
  background: #ccc;
  left: 26px;
  animation: lds-ellipsis2 0.6s infinite;
}

.lds-ellipsis div:nth-child(4) {
  background: #ddd;
  left: 45px;
  animation: lds-ellipsis3 0.6s infinite;
}

@keyframes lds-ellipsis1 {
  0% {
    transform: scale(0);
  }

  100% {
    transform: scale(1);
  }
}

@keyframes lds-ellipsis3 {
  0% {
    transform: scale(1);
  }

  100% {
    transform: scale(0);
  }
}

@keyframes lds-ellipsis2 {
  0% {
    transform: translate(0, 0);
  }

  100% {
    transform: translate(19px, 0);
  }
}
</style>
